import { useState } from 'react';
import { Bell, Send, AlertCircle, CheckCircle, Loader2, Info } from 'lucide-react';
import { adminApi } from '../stores/adminStore';
import { toast } from 'sonner';

interface BroadcastResult {
  success: boolean;
  message: string;
  sentCount: number;
  failedCount: number;
  totalDevices: number;
}

// Available screens in mobile app (verified from App.tsx)
const AVAILABLE_SCREENS = [
  { value: 'Home', label: '🏠 Home Screen', description: 'Main dashboard' },
  { value: 'Upload', label: '📤 Upload', description: 'Upload & generate metadata' },
  { value: 'History', label: '📚 History', description: 'Metadata collections' },
  { value: 'Account', label: '👤 Account', description: 'User account settings' },
  { value: 'Pricing', label: '💳 Pricing', description: 'Credits & membership plans' },
  { value: 'AITools', label: '🤖 AI Tools', description: 'AI tools menu' },
  { value: 'PromptGenerator', label: '✍️ Prompt Generator', description: 'Generate AI prompts' },
  { value: 'PromptCollections', label: '📝 Prompt Collections', description: 'Saved prompts' },
  { value: 'ImageGeneratorV2', label: '🎨 Image Generator', description: 'Generate AI images' },
  { value: 'ImageCollections', label: '🖼️ Image Collections', description: 'Generated images' },
  { value: 'UpscalerV2', label: '⬆️ Upscaler', description: 'Upscale images' },
  { value: 'UpscaledBatchCollections', label: '📦 Upscaled Collections', description: 'View upscaled images' },
  { value: 'GoalSetting', label: '🎯 Goal Setting', description: 'Set monthly goals' },
  { value: 'FTPManagement', label: '🌐 FTP Management', description: 'Manage FTP accounts' },
];

export default function PushNotificationsPage() {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [targetScreen, setTargetScreen] = useState('Home');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<BroadcastResult | null>(null);

  const handleSendBroadcast = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error('Title and message are required');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await adminApi.post<BroadcastResult>('/api/v1/notifications/broadcast', {
        title: title.trim(),
        body: body.trim(),
        data: {
          screen: targetScreen,
          timestamp: new Date().toISOString(),
        },
      });

      setResult(response.data);
      
      // Clear form on success
      if (response.data.success) {
        toast.success(`Broadcast sent to ${response.data.sentCount} devices!`);
        setTitle('');
        setBody('');
        setTargetScreen('Home');
      } else {
        toast.error('Failed to send broadcast');
      }
    } catch (err: any) {
      console.error('Failed to send broadcast:', err);
      const errorMessage = typeof err.response?.data?.error === 'string' 
        ? err.response.data.error 
        : err.response?.data?.error?.message || err.message || 'Failed to send broadcast notification';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setTitle('');
    setBody('');
    setTargetScreen('Home');
    setResult(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Bell className="w-8 h-8 text-blue-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Push Notifications</h1>
          <p className="text-gray-600 mt-1">
            Send push notifications to all registered mobile app users
          </p>
        </div>
      </div>

      {/* Main Form Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="space-y-4">
          {/* Title Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notification Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., New Feature Available!"
              disabled={loading}
              maxLength={100}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            <p className="text-xs text-gray-500 mt-1">{title.length}/100 characters</p>
          </div>

          {/* Body Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notification Message
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="e.g., Check out our new AI upscaler feature for even better image quality!"
              disabled={loading}
              maxLength={500}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            <p className="text-xs text-gray-500 mt-1">{body.length}/500 characters</p>
          </div>

          {/* Target Screen Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Target Screen (Deep Link)
            </label>
            <select
              value={targetScreen}
              onChange={(e) => setTargetScreen(e.target.value)}
              disabled={loading}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              {AVAILABLE_SCREENS.map((screen) => (
                <option key={screen.value} value={screen.value}>
                  {screen.label} - {screen.description}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              When users tap the notification, they will be directed to this screen
            </p>
          </div>

          {/* Result Alert */}
          {result && (
            <div className={`p-4 rounded-lg border ${
              result.success && result.sentCount > 0
                ? 'bg-green-50 border-green-200'
                : 'bg-yellow-50 border-yellow-200'
            }`}>
              <div className="flex items-start gap-3">
                {result.success && result.sentCount > 0 ? (
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className={`font-semibold ${
                    result.success && result.sentCount > 0 ? 'text-green-900' : 'text-yellow-900'
                  }`}>
                    {result.message}
                  </p>
                  <p className={`text-sm mt-1 ${
                    result.success && result.sentCount > 0 ? 'text-green-800' : 'text-yellow-800'
                  }`}>
                    Sent to {result.sentCount} of {result.totalDevices} devices
                    {result.failedCount > 0 && ` (${result.failedCount} failed)`}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleSendBroadcast}
              disabled={loading || !title.trim() || !body.trim()}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Send Broadcast
                </>
              )}
            </button>
            <button
              onClick={handleClear}
              disabled={loading}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Best Practices Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-blue-900 mb-2">
              Best Practices
            </h3>
            <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
              <li>Keep titles short and attention-grabbing (under 50 characters)</li>
              <li>Make messages clear and actionable</li>
              <li>Avoid sending too many notifications (max 2-3 per week)</li>
              <li>Test with a small group first if possible</li>
              <li>Send during appropriate hours (9 AM - 9 PM local time)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
